<template>
  <TransitionRoot appear :show="isOpen" as="template"
    @before-enter="logTransition('root', 'before-enter')"
    @after-enter="logTransition('root', 'after-enter')"
    @before-leave="logTransition('root', 'before-leave')"
    @after-leave="logTransition('root', 'after-leave')"
  >
    <Dialog as="div" @close="$emit('close')" class="relative z-30">
      <!-- Backdrop -->
      <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0"
        enter-to="opacity-100" leave="duration-200 ease-in" leave-from="opacity-100"
        leave-to="opacity-0"
        @before-enter="logTransition('backdrop', 'before-enter')"
        @after-enter="logTransition('backdrop', 'after-enter')"
        @before-leave="logTransition('backdrop', 'before-leave')"
        @after-leave="logTransition('backdrop', 'after-leave')"
      >
        <div class="fixed inset-0 bs-backdrop" />
      </TransitionChild>

      <div class="fixed inset-x-0 top-0 overflow-y-auto transition-[height] duration-300 ease-out"
        :style="{ height: viewportHeight + 'px' }">
        <div class="flex min-h-full items-end sm:items-center justify-center sm:p-4">
          <!-- Panel -->
          <TransitionChild as="template" enter="duration-350 ease-out"
            enter-from="opacity-0 translate-y-8" enter-to="opacity-100 translate-y-0"
            leave="duration-200 ease-in" leave-from="opacity-100 translate-y-0"
            leave-to="opacity-0 translate-y-8"
            @before-enter="logTransition('panel', 'before-enter')"
            @after-enter="logTransition('panel', 'after-enter')"
            @before-leave="logTransition('panel', 'before-leave')"
            @after-leave="logTransition('panel', 'after-leave')"
          >
            <DialogPanel
              class="w-full transform overflow-hidden rounded-t-[22px] sm:rounded-[22px] bs-panel shadow-2xl transition-all pb-safe"
              :class="panelClass">

              <!-- Drag indicator (mobile only) -->
              <div class="flex justify-center sm:hidden" :class="size === 'sm' ? 'mb-4 pt-0' : 'pt-3 pb-1'" >
                <div class="w-9 h-1 rounded-full bs-handle"></div>
              </div>

              <!-- Standard header for lg sheets with title -->
              <div v-if="title && size === 'lg'" class="flex-shrink-0">
                <div class="bs-header">
                  <!-- Mobile: X left, title centered -->
                  <button @click="$emit('close')" class="xl:hidden text-muted-foreground hover:text-foreground transition-colors">
                    <XMarkIcon class="h-5 w-5" />
                  </button>
                  <div class="xl:flex xl:items-center xl:justify-between xl:w-full">
                    <div>
                      <h3 class="bs-header-title">{{ title }}</h3>
                      <p v-if="subtitle" class="hidden xl:block text-[12px] text-muted-foreground mt-0.5">
                        {{ subtitle }}
                      </p>
                    </div>
                    <!-- Desktop: close button circle -->
                    <button @click="$emit('close')" class="bs-close-desktop hidden xl:flex">
                      <XMarkIcon class="h-4 w-4" />
                    </button>
                  </div>
                  <div class="w-5 xl:hidden"></div>
                </div>

                <!-- Extra header content (period nav, stepper, etc.) -->
                <slot name="header-extra" />
              </div>

              <slot />

            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import logger from '../../utils/logger'

const props = withDefaults(defineProps<{
  isOpen: boolean
  size?: 'sm' | 'lg'
  title?: string
  subtitle?: string
}>(), {
  size: 'sm',
})

defineEmits<{
  close: []
}>()

const logTransition = (element: string, event: string) => {
  logger.debug(`[BaseSheet] ${element} ${event}`, performance.now().toFixed(1))
}

const panelClass = computed(() => {
  if (props.size === 'lg') {
    return 'sm:max-w-[560px] xl:max-w-[600px] h-[85vh] max-h-[92vh] sm:h-[70vh] sm:max-h-[80vh] flex flex-col'
  }
  return 'sm:max-w-[360px] p-6 pt-5'
})

// ── Viewport tracking (soft keyboard on mobile) ──
const viewportHeight = ref(window.innerHeight)

const onViewportResize = () => {
  if (window.visualViewport) {
    viewportHeight.value = window.visualViewport.height
  }
}

onMounted(() => {
  window.visualViewport?.addEventListener('resize', onViewportResize)
  window.visualViewport?.addEventListener('scroll', onViewportResize)
})

onUnmounted(() => {
  window.visualViewport?.removeEventListener('resize', onViewportResize)
  window.visualViewport?.removeEventListener('scroll', onViewportResize)
})
</script>

<style scoped>
/* ── Header (lg sheets) ── */
.bs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 20px 12px;
}

@media (min-width: 1280px) {
  .bs-header {
    padding: 20px 24px 16px;
    border-bottom: 3px solid transparent;
    border-image: linear-gradient(90deg, var(--apex-cyan), var(--apex-cyan-alt)) 1;
  }
}

.bs-header-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--foreground);
  letter-spacing: -0.01em;
  text-align: center;
}

@media (min-width: 1280px) {
  .bs-header-title {
    text-align: left;
    font-size: 16px;
  }
}

.bs-close-desktop {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);
  background: var(--surface-sunken);
  transition: all var(--duration-fast) ease;
  flex-shrink: 0;
}

.bs-close-desktop:hover {
  background: var(--surface-hover);
  color: var(--foreground);
  transform: scale(1.05);
}

/* ── Light mode ── */
.bs-backdrop {
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
.bs-panel {
  background: linear-gradient(170deg, #FFFFFF 0%, #F6FAFC 100%);
  border: 1px solid rgba(6, 200, 255, 0.10);
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.12),
    0 8px 24px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
}
.bs-handle {
  background: rgba(0, 0, 0, 0.10);
}

/* ── Dark mode ── */
:is(.dark *).bs-backdrop {
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}
:is(.dark *).bs-panel {
  background: linear-gradient(170deg, #232528 0%, #1C1E21 100%);
  border: 1px solid rgba(6, 200, 255, 0.08);
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.5),
    0 8px 24px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}
:is(.dark *).bs-handle {
  background: rgba(255, 255, 255, 0.14);
}

.duration-350 {
  transition-duration: 350ms;
}
</style>
